import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Clock, ChevronDown, Calendar } from 'lucide-react';

// Friendly preset times — computed at open so they always reflect "now."
function presets() {
  const now = new Date();
  const out = [];

  // In one hour
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
  out.push({ label: 'In 1 hour', dt: inOneHour });

  // Today at 5 PM (only if still future)
  const todayFive = new Date(now);
  todayFive.setHours(17, 0, 0, 0);
  if (todayFive.getTime() > now.getTime() + 5 * 60 * 1000) {
    out.push({ label: 'Later today (5:00 PM)', dt: todayFive });
  }

  // Tomorrow at 9 AM
  const tomorrowNine = new Date(now);
  tomorrowNine.setDate(tomorrowNine.getDate() + 1);
  tomorrowNine.setHours(9, 0, 0, 0);
  out.push({ label: 'Tomorrow morning (9:00 AM)', dt: tomorrowNine });

  // Next Monday at 9 AM (skip if it's already Monday morning)
  const nextMon = new Date(now);
  const dayOfWeek = nextMon.getDay(); // 0 = Sunday, 1 = Monday
  const daysUntilMon = (1 - dayOfWeek + 7) % 7 || 7;
  nextMon.setDate(nextMon.getDate() + daysUntilMon);
  nextMon.setHours(9, 0, 0, 0);
  out.push({ label: `Next Monday (${nextMon.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} 9:00 AM)`, dt: nextMon });

  return out;
}

function formatDt(dt) {
  if (!dt) return '';
  const sameDay = dt.toDateString() === new Date().toDateString();
  return sameDay
    ? dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Compact "schedule send" picker. Shows a clock icon button; clicking opens
 * a dropdown with quick presets + a "Pick date & time…" mode. When the
 * caller has a scheduledFor set, the trigger button shows the time.
 *
 * Props:
 *   scheduledFor — Date | null
 *   onChange     — (Date|null) => void
 *   disabled
 */
export default function ScheduleSendMenu({ scheduledFor, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const wrapperRef = useRef(null);

  // Reset state on close so reopening starts fresh.
  useEffect(() => {
    if (!open) {
      setCustom(false);
      setCustomValue('');
    }
  }, [open]);

  // Outside click → close.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // Re-compute presets each time the menu re-opens so they reflect "now."
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => presets(), [open]);

  const pick = (dt) => {
    onChange?.(dt);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={scheduledFor ? `Scheduled for ${formatDt(scheduledFor)}` : 'Schedule send for later'}
        className={`flex items-center gap-1 p-1.5 rounded-lg transition disabled:opacity-50 ${
          scheduledFor
            ? 'text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/30'
            : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800'
        }`}
      >
        <Clock size={14} />
        {scheduledFor && (
          <span className="text-[11px] font-medium hidden sm:inline">{formatDt(scheduledFor)}</span>
        )}
        <ChevronDown size={10} className="opacity-60" />
      </button>

      {open && (
        <div className="absolute z-40 bottom-full mb-2 right-0 w-64 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden">
          {!custom ? (
            <>
              <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100 dark:border-slate-800">
                Schedule send
              </div>
              <ul className="py-1">
                {items.map((p) => (
                  <li key={p.label}>
                    <button
                      onClick={() => pick(p.dt)}
                      className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                    >
                      {p.label}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => setCustom(true)}
                    className="w-full px-3 py-2 text-left text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition flex items-center gap-2"
                  >
                    <Calendar size={12} /> Pick date &amp; time…
                  </button>
                </li>
                {scheduledFor && (
                  <li>
                    <button
                      onClick={() => pick(null)}
                      className="w-full px-3 py-2 text-left text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition"
                    >
                      Send immediately instead
                    </button>
                  </li>
                )}
              </ul>
            </>
          ) : (
            <div className="p-3 space-y-2">
              <label className="block text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">When?</label>
              <input
                type="datetime-local"
                value={customValue}
                min={new Date(Date.now() + 60 * 1000).toISOString().slice(0, 16)}
                onChange={(e) => setCustomValue(e.target.value)}
                className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  onClick={() => setCustom(false)}
                  className="px-2.5 py-1 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (!customValue) return;
                    const dt = new Date(customValue);
                    if (Number.isNaN(dt.getTime()) || dt <= new Date()) return;
                    pick(dt);
                  }}
                  disabled={!customValue}
                  className="px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium rounded-lg"
                >
                  Schedule
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
